import AssignmentTestsService from '../../services/assignment-tests.service'
import { openNotificationWithIcon } from '../../utils/notification.helper'
import { ASSIGNMENT_TEST_TYPES } from '../../constants/assignment-test-types'
import AssignmentService from '../../services/assignment.service'
import { handleErrors } from '../../utils/request.helper'

/***************************************** INITIAL STATE ********************************************/
const INITIAL_REFERENCE_SOLUTION = {
    name: '',
    code: '',
    isWaiting: false,
}

export const INITIAL_STATE = {
    showCreateStdinTestModal: false,
    showEditStdinTestModal: false,
    showCreateFunctionInTestModal: false,
    showEditFunctionInTestModal: false,
    showCreateDatabaseInTestModal: false,
    showEditDatabaseInTestModal: false,
    showCreateFrameworkTestModal: false,
    showEditFrameworkTestModal: false,
    unitTests: [],
    referenceSolution: INITIAL_REFERENCE_SOLUTION,
    currentlyEditingTestId: null,
}

/***************************************** ACTION TYPES *********************************************/
const reducerName = 'UNIT_TESTS_'
const actionTypes = {
    // REFERENCE SOLUTION
    SET_REFERENCE_SOLUTION_NAME: reducerName + 'SET_REFERENCE_SOLUTION_NAME',
    SET_REFERENCE_SOLUTION_CODE: reducerName + 'SET_REFERENCE_SOLUTION_CODE',
    SET_IS_WAITING_FOR_REFERENCE_SOLUTION: reducerName + 'SET_IS_WAITING_FOR_REFERENCE_SOLUTION',
    // STDIN TEST MODAL
    SET_SHOW_CREATE_STDIN_TEST_MODAL: `${reducerName}SET_SHOW_CREATE_STDIN_TEST_MODAL`,
    SET_SHOW_EDIT_STDIN_TEST_MODAL: `${reducerName}SET_SHOW_EDIT_STDIN_TEST_MODAL`,
    // FUNCTION IN TEST MODAL
    SET_SHOW_CREATE_FUNCTION_IN_TEST_MODAL: `${reducerName}SET_SHOW_CREATE_FUNCTION_IN_TEST_MODAL`,
    SET_SHOW_EDIT_FUNCTION_IN_TEST_MODAL: `${reducerName}SET_SHOW_EDIT_FUNCTION_IN_TEST_MODAL`,
    // FRAMEWORK TEST MODAL
    SET_SHOW_CREATE_FRAMEWORK_TEST_MODAL: `${reducerName}SET_SHOW_CREATE_FRAMEWORK_TEST_MODAL`,
    SET_SHOW_EDIT_FRAMEWORK_TEST_MODAL: `${reducerName}SET_SHOW_EDIT_FRAMEWORK_TEST_MODAL`,
    //DATABASE IN TEST MODAL
    SET_SHOW_CREATE_DATABASE_IN_TEST_MODAL: `${reducerName}SET_SHOW_CREATE_DATABASE_IN_TEST_MODAL`,
    SET_SHOW_EDIT_DATABASE_IN_TEST_MODAL: `${reducerName}SET_SHOW_EDIT_DATABASE_IN_TEST_MODAL`,
    // UNIT TESTS TABLE
    SET_UNIT_TESTS: `${reducerName}SET_UNIT_TESTS`,
    SET_CURRENTLY_EDITING_TEST_ID: `${reducerName}SET_CURRENTLY_EDITING_TEST_ID`,
}

/******************************************** ACTIONS ***********************************************/
export const actions = {
    // REFERENCE SOLUTION
    setReferenceSolutionName: value => ({
        type: actionTypes.SET_REFERENCE_SOLUTION_NAME,
        payload: { value },
    }),
    setReferenceSolutionCode: value => ({
        type: actionTypes.SET_REFERENCE_SOLUTION_CODE,
        payload: { value },
    }),
    setIsWaitingForReferenceSolution: value => ({
        type: actionTypes.SET_IS_WAITING_FOR_REFERENCE_SOLUTION,
        payload: { value },
    }),
    fetchReferenceSolution: token => async dispatch => {
        dispatch(actions.setIsWaitingForReferenceSolution(true))
        await handleErrors(async () => {
            const getReferenceSolutionResponse = await AssignmentService.getReferenceSolution(token)
            dispatch(actions.setReferenceSolutionName(getReferenceSolutionResponse.data.teacher_solution_filename))
            dispatch(actions.setReferenceSolutionCode(getReferenceSolutionResponse.data.teacher_solution_content))
        })
        dispatch(actions.setIsWaitingForReferenceSolution(false))
    },
    uploadReferenceSolution: (token, file) => async dispatch => {
        return await handleErrors(async () => {
            const uploadReferenceSolutionResponse = await AssignmentService.uploadReferenceSolution(token, file)
            dispatch(actions.setReferenceSolutionName(uploadReferenceSolutionResponse.data.teacher_solution_filename))
            dispatch(actions.setReferenceSolutionCode(uploadReferenceSolutionResponse.data.teacher_solution_content))
            openNotificationWithIcon('success', 'Uploaded successfully')
        })
    },
    uploadFixtureFiles: (token, fixtureFilesZip) => async () => {
        return await handleErrors(async () => {
            const { data: fixtureFilesPath } = await AssignmentService.uploadTestFixtureFiles(token, fixtureFilesZip)
            return fixtureFilesPath
        })
    },
    setShowCreateStdinTestModal: value => ({
        type: actionTypes.SET_SHOW_CREATE_STDIN_TEST_MODAL,
        payload: { value },
    }),
    setShowEditStdinTestModal: value => ({
        type: actionTypes.SET_SHOW_EDIT_STDIN_TEST_MODAL,
        payload: { value },
    }),
    createStdinTest: (token, fields, setResponseMessage) => async (dispatch, getState) => {
        return await handleErrors(async () => {
            const createTestResponse = await AssignmentTestsService.createTest(
                token,
                fields.name,
                ASSIGNMENT_TEST_TYPES.STDIN.value,
                {
                    input: fields.input,
                    exact_match: fields.exactMatch,
                    ignore_case: fields.ignoreCase,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
            )
            dispatch(actions.setUnitTests([...getState().unitTests.unitTests, createTestResponse.data]))
            openNotificationWithIcon('success', 'Created successfully')
            return createTestResponse.data
        }, setResponseMessage)
    },
    setShowCreateFunctionInTestModal: value => ({
        type: actionTypes.SET_SHOW_CREATE_FUNCTION_IN_TEST_MODAL,
        payload: { value },
    }),
    setShowEditFunctionInTestModal: value => ({
        type: actionTypes.SET_SHOW_EDIT_FUNCTION_IN_TEST_MODAL,
        payload: { value },
    }),
    createFunctionInTest: (token, fields, setResponseMessage) => async (dispatch, getState) => {
        return await handleErrors(async () => {
            const createTestResponse = await AssignmentTestsService.createTest(
                token,
                fields.name,
                ASSIGNMENT_TEST_TYPES.FUNCTION_IN.value,
                {
                    input: fields.input,
                    function_name: fields.functionName,
                    exact_match: fields.exactMatch,
                    ignore_case: fields.ignoreCase,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
            )
            dispatch(actions.setUnitTests([...getState().unitTests.unitTests, createTestResponse.data]))
            openNotificationWithIcon('success', 'Created successfully')
            return createTestResponse.data
        }, setResponseMessage)
    },
    setShowCreateFrameworkTestModal: value => ({
        type: actionTypes.SET_SHOW_CREATE_FRAMEWORK_TEST_MODAL,
        payload: { value },
    }),
    setShowEditFrameworkTestModal: value => ({
        type: actionTypes.SET_SHOW_EDIT_FRAMEWORK_TEST_MODAL,
        payload: { value },
    }),
    setShowCreateDatabaseInTestModal: value => ({
        type: actionTypes.SET_SHOW_CREATE_DATABASE_IN_TEST_MODAL,
        payload: { value },
    }),
    setShowEditDatabaseInTestModal: value => ({
        type: actionTypes.SET_SHOW_EDIT_DATABASE_IN_TEST_MODAL,
        payload: { value },
    }),
    createDatabaseInTest: (token, fields, setResponseMessage) => async (dispatch, getState) => {
        return await handleErrors(async () => {
            const createTestResponse = await AssignmentTestsService.createTest(
                token,
                fields.name,
                ASSIGNMENT_TEST_TYPES.FUNCTION_IN.value, //database in tests on the backend are treated as function in tests
                {
                    input: fields.input, //database dump is stored in the input field on the backend
                    exact_match: fields.exactMatch,
                    ignore_case: fields.ignoreCase,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
            )
            dispatch(actions.setUnitTests([...getState().unitTests.unitTests, createTestResponse.data]))
            openNotificationWithIcon('success', 'Created successfully')
            return createTestResponse.data
        }, setResponseMessage)
    },
    createFrameworkTest: (token, fields, setResponseMessage) => async (dispatch, getState) => {
        return await handleErrors(async () => {
            const createTestResponse = await AssignmentTestsService.createTest(
                token,
                fields.name,
                ASSIGNMENT_TEST_TYPES.FRAMEWORK.value,
                {
                    framework: fields.framework,
                    test_suite: fields.testSuite,
                    main_class_name: fields.mainClassName,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
            )
            dispatch(actions.setUnitTests([...getState().unitTests.unitTests, createTestResponse.data]))
            openNotificationWithIcon('success', 'Created successfully')
            return createTestResponse.data
        }, setResponseMessage)
    },
    createFlexibleTest: (token, fields, setResponseMessage) => async (dispatch, getState) => {
        return await handleErrors(async () => {
            const createTestResponse = await AssignmentTestsService.createTest(
                token,
                fields.name,
                ASSIGNMENT_TEST_TYPES.FRAMEWORK.value,
                {
                    framework: fields.framework,
                    test_suite: fields.testSuite,
                    main_class_name: fields.mainClassName,
                    input: fields.inputs, //inputs are stored in the input field on the backend
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
            )
            dispatch(actions.setUnitTests([...getState().unitTests.unitTests, createTestResponse.data]))
            openNotificationWithIcon('success', 'Created successfully')
            return createTestResponse.data
        }, setResponseMessage)
    },
    // UNIT TESTS TABLE
    setUnitTests: value => ({
        type: actionTypes.SET_UNIT_TESTS,
        payload: { value },
    }),
    setCurrentlyEditingTestId: value => ({
        type: actionTypes.SET_CURRENTLY_EDITING_TEST_ID,
        payload: { value },
    }),
    fetchUnitTests: token => async dispatch => {
        return await handleErrors(async () => {
            const getTestsResponse = await AssignmentTestsService.getTests(token)
            dispatch(actions.setUnitTests(getTestsResponse.data))
        })
    },
    deleteUnitTest: id => async (dispatch, getState) => {
        return await handleErrors(async () => {
            await AssignmentTestsService.deleteTest(id)
            dispatch(actions.setUnitTests(getState().unitTests.unitTests.filter(unitTest => unitTest.id !== id)))
            openNotificationWithIcon('success', 'Deleted successfully')
        })
    },
    updateUnitTest: (name, data, cpuLimit, memoryLimit, setResponseMessage) => async (dispatch, getState) => {
        return await handleErrors(async () => {
            const updateTestResponse = await AssignmentTestsService.updateTest(
                getState().unitTests.currentlyEditingTestId,
                name,
                data,
                cpuLimit,
                memoryLimit,
            )
            const unitTestsUpdated = getState().unitTests.unitTests.map(ut =>
                ut.id === getState().unitTests.currentlyEditingTestId ? { ...updateTestResponse.data } : ut,
            )
            dispatch(actions.setUnitTests(unitTestsUpdated))
            openNotificationWithIcon('success', 'Updated successfully')
            return updateTestResponse.data
        }, setResponseMessage)
    },
    updateStdinUnitTest: (token, fields, setResponseMessage) => (dispatch) => {
        return dispatch(
            actions.updateUnitTest(
                fields.name,
                {
                    input: fields.input,
                    ignore_case: fields.ignoreCase,
                    exact_match: fields.exactMatch,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
                setResponseMessage
            ),
        )
    },
    updateFunctionInUnitTest: (token, fields, setResponseMessage) => (dispatch) => {
        return dispatch(
            actions.updateUnitTest(
                fields.name,
                {
                    input: fields.input,
                    function_name: fields.functionName,
                    ignore_case: fields.ignoreCase,
                    exact_match: fields.exactMatch,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
                setResponseMessage
            ),
        )
    },
    updateFrameworkUnitTest: (token, fields, setResponseMessage) => (dispatch, getState) => {
        return dispatch(
            actions.updateUnitTest(
                fields.name,
                {
                    framework: fields.framework,
                    test_suite: fields.testSuite,
                    test_cases: getState().unitTests.unitTests.find(
                        unitTest => unitTest.id === getState().unitTests.currentlyEditingTestId,
                    ).test_data.test_cases,
                    main_class_name: fields.mainClassName,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
                setResponseMessage
            ),
        )
    },
    updateFlexibleUnitTest: (token, fields, setResponseMessage) => (dispatch, getState) => {
        return dispatch(
            actions.updateUnitTest(
                fields.name,
                {
                    framework: fields.framework,
                    test_suite: fields.testSuite,
                    input: fields.inputs, //inputs are stored in the input field on the backend
                    test_cases: getState().unitTests.unitTests.find(
                        unitTest => unitTest.id === getState().unitTests.currentlyEditingTestId,
                    ).test_data.test_cases,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
                setResponseMessage
            ),
        )
    },
    updateDatabaseInUnitTest: (token, fields, setResponseMessage) => (dispatch) => {
        return dispatch(
            actions.updateUnitTest(
                fields.name,
                {
                    input: fields.input,
                    ignore_case: fields.ignoreCase,
                    exact_match: fields.exactMatch,
                    additional_files_path: fields.fixtureFilesPath,
                },
                fields.cpuTimeLimit,
                fields.memoryLimit,
                setResponseMessage
            ),
        )
    },
    importConfig: (token, file) => async dispatch => {
        return await handleErrors(async () => {
            const importConfigResponse = await AssignmentService.importConfig(token, file)
            dispatch(actions.setUnitTests(importConfigResponse.data))
            openNotificationWithIcon('success', 'Imported successfully')
        })
    },
}

/******************************************* REDUCER ************************************************/
const unitTestsReducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        // REFERENCE SOLUTION
        case actionTypes.SET_REFERENCE_SOLUTION_NAME:
            return {
                ...state,
                referenceSolution: {
                    ...state.referenceSolution,
                    name: action.payload.value,
                },
            }

        case actionTypes.SET_REFERENCE_SOLUTION_CODE:
            return {
                ...state,
                referenceSolution: {
                    ...state.referenceSolution,
                    code: action.payload.value,
                },
            }

        case actionTypes.SET_IS_WAITING_FOR_REFERENCE_SOLUTION:
            return {
                ...state,
                referenceSolution: {
                    ...state.referenceSolution,
                    isWaiting: action.payload.value,
                },
            }

        // STDIN TEST MODAL
        case actionTypes.SET_SHOW_CREATE_STDIN_TEST_MODAL:
            return {
                ...state,
                showCreateStdinTestModal: action.payload.value,
            }

        case actionTypes.SET_SHOW_EDIT_STDIN_TEST_MODAL:
            return {
                ...state,
                showEditStdinTestModal: action.payload.value,
            }

        // FUNCTION IN TEST MODAL
        case actionTypes.SET_SHOW_CREATE_FUNCTION_IN_TEST_MODAL:
            return {
                ...state,
                showCreateFunctionInTestModal: action.payload.value,
            }

        case actionTypes.SET_SHOW_EDIT_FUNCTION_IN_TEST_MODAL:
            return {
                ...state,
                showEditFunctionInTestModal: action.payload.value,
            }

        // FRAMEWORK TEST MODAL
        case actionTypes.SET_SHOW_CREATE_FRAMEWORK_TEST_MODAL:
            return {
                ...state,
                showCreateFrameworkTestModal: action.payload.value,
            }

        case actionTypes.SET_SHOW_EDIT_FRAMEWORK_TEST_MODAL:
            return {
                ...state,
                showEditFrameworkTestModal: action.payload.value,
            }

        // DATABASE IN TEST MODAL
        case actionTypes.SET_SHOW_CREATE_DATABASE_IN_TEST_MODAL:
            return {
                ...state,
                showCreateDatabaseInTestModal: action.payload.value,
            }

        case actionTypes.SET_SHOW_EDIT_DATABASE_IN_TEST_MODAL:
            return {
                ...state,
                showEditDatabaseInTestModal: action.payload.value,
            }

        // UNIT TESTS TABLE
        case actionTypes.SET_UNIT_TESTS:
            return {
                ...state,
                unitTests: action.payload.value,
            }

        case actionTypes.SET_CURRENTLY_EDITING_TEST_ID:
            return {
                ...state,
                currentlyEditingTestId: action.payload.value,
            }

        default:
            return state
    }
}

export default unitTestsReducer
